import { MdBusiness, MdEmail, MdLocationOn, MdPhone } from "react-icons/md";
import ProfilePictureUpload from "../../../components/forms/ProfilePictureUpload";
import { User } from "../../../features/users/usersTypes";
import ProfileShareLinksSection from "./ProfileShareLinksSection";

type ProfileIdentityCardProps = {
  user?: User;
  mobileNumber?: string;
  onPictureChange: (file: File | null) => void;
};

const ProfileIdentityCard = ({
  user,
  mobileNumber,
  onPictureChange,
}: ProfileIdentityCardProps) => {
  const data = user as User & {
    address_line_2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: { label?: string } | string;
  };

  const companyName = user?.organization?.name?.trim() || "";
  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
    user?.full_name?.trim() ||
    "Your profile";
  const initials = displayName
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const address = [
    (data as { organization?: { address_line_1?: string } })?.organization
      ?.address_line_1,
    data?.address_line_2,
    data?.city,
    data?.state,
    data?.zipCode,
    typeof data?.country === "object"
      ? data.country?.label
      : (data?.country as string | undefined),
  ]
    .filter(Boolean)
    .join(", ");

  const contactItems = [
    user?.email
      ? { icon: MdEmail, label: "Email", value: user.email }
      : null,
    mobileNumber
      ? { icon: MdPhone, label: "Mobile", value: mobileNumber }
      : null,
    address
      ? { icon: MdLocationOn, label: "Address", value: address }
      : null,
  ].filter(Boolean) as {
    icon: typeof MdEmail;
    label: string;
    value: string;
  }[];

  return (
    <aside className="profile-identity">
      <div className="profile-identity__body">
        <div className="profile-identity__hero">
          <div className="profile-identity__hero-cover" aria-hidden />

          <div className="profile-identity__hero-main">
            <ProfilePictureUpload
              name="picture"
              onChange={onPictureChange}
              currentImageUrl={user?.picture}
              containerClass="profile-identity__avatar"
              variant="sidebar"
              initials={initials}
            />

            <h2 className="profile-identity__name">{displayName}</h2>

            <div
              className={`profile-identity__org ${
                companyName ? "" : "profile-identity__org--empty"
              }`}
            >
              <MdBusiness aria-hidden />
              <span>{companyName || "Organization not set"}</span>
            </div>
          </div>
        </div>

        {contactItems.length > 0 && (
          <div className="profile-identity__panel">
            <h3 className="profile-identity__panel-title">Contact</h3>
            <ul className="profile-identity__meta">
              {contactItems.map(({ icon: Icon, label, value }) => (
                <li key={label}>
                  <span className="profile-identity__meta-icon" aria-hidden>
                    <Icon />
                  </span>
                  <div className="profile-identity__meta-content">
                    <span className="profile-identity__meta-label">{label}</span>
                    <span className="profile-identity__meta-value">{value}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <ProfileShareLinksSection user={user} />
      </div>
    </aside>
  );
};

export default ProfileIdentityCard;
